/**
 * VADSpeechEngine - VAD-driven speech recognition engine
 * 
 * Combines Voice Activity Detection with speech recognition to provide
 * intelligent start/stop behavior based on actual speech detection.
 */

import { SpeechEngine, SpeechStates, SpeechEvents } from './SpeechEngine.js';
import { MinimalStateMachine, MinimalStates, TransitionReasons } from './MinimalStateMachine.js';
import { VoiceActivityDetector } from './VoiceActivityDetector.js';
import { BrowserSTT } from './BrowserSTT.js';
import { StreamingSTT } from './StreamingSTT.js';
import { TriggerDetector } from './TriggerDetector.js';
import micManager from './MicManager.js';

export class VADSpeechEngine extends SpeechEngine {
    static isSupported() {
        try {
            return 'AudioContext' in window && 
                   'MediaDevices' in navigator && 
                   'AudioWorklet' in window.AudioContext.prototype &&
                   'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
        } catch (error) {
            console.warn('VADSpeechEngine: Support check failed:', error);
            return false;
        }
    }

    constructor(options = {}) {
        super();
        
        this.options = {
            // VAD settings (lowered for better sensitivity)
            energyThreshold: options.energyThreshold || 0.005,
            silenceThreshold: options.silenceThreshold || 0.002,
            silenceDuration: options.silenceDuration || 1000,
            minSpeechDuration: options.minSpeechDuration || 200,
            
            // Speech engine settings
            language: options.language || 'en-US',
            maxAlternatives: options.maxAlternatives || 1,
            speechEngineType: options.speechEngineType || 'browser', // Default to browser, can be 'streaming'
            
            // VAD integration
            vadEnabled: options.vadEnabled !== false,
            autoStartOnSpeech: options.autoStartOnSpeech !== false,
            autoStopOnSilence: options.autoStopOnSilence !== false,
            
            ...options
        };
        
        this.vad = null;
        this.speechEngine = null;
        this.triggerDetector = null;
        this.stateMachine = new MinimalStateMachine({
            maxHistorySize: options.maxHistorySize || 20
        });
        
        this.isSupported = false;
        this.isInitialized = false;
        this.isVADAvailable = false;
        this.vadInitializationFailed = false;
        this.partialBuffer = '';
        this.isManualStop = false;
        this.speechStartTime = null;
        
        // VAD gating state
        this.isVADControlled = true; // VAD controls start/stop
        this.pendingRestart = false; // Track if we need to restart after BrowserSTT ends
        this.speechSegments = []; // Buffer for speech segments
        this.currentSegment = null; // Current speech segment
        
        this.initializeComponents();
    }

    /**
     * Initialize VAD and speech engine components with graceful fallback
     */
    async initializeComponents() {
        try {
            console.log('🎤 VADSpeechEngine: Initializing components...');
            
            // First, ensure we have microphone access
            console.log('🎤 VADSpeechEngine: Acquiring microphone for validation...');
            const mediaStream = await micManager.acquire();
            
            if (!mediaStream || !mediaStream.active) {
                throw new Error('Failed to acquire active microphone stream');
            }
            
            // Validate AudioContext state
            const audioContext = micManager.getAudioContext();
            if (!audioContext || audioContext.state !== 'running') {
                throw new Error(`AudioContext not in running state: ${audioContext?.state || 'null'}`);
            }
            
            console.log('🎤 VADSpeechEngine: Microphone and AudioContext validated');
            
            // Check VAD support
            if (this.options.vadEnabled && !VoiceActivityDetector.isSupported()) {
                console.warn('🎤 VADSpeechEngine: VAD not supported, falling back to basic mode');
                this.options.vadEnabled = false;
                this.emit(SpeechEvents.VAD_UNAVAILABLE, { reason: 'not_supported' });
            }
            
            // Initialize VAD if enabled
            if (this.options.vadEnabled) {
                try {
                    console.log('🎤 VADSpeechEngine: Initializing VAD...');
                    this.vad = new VoiceActivityDetector({
                        energyThreshold: this.options.energyThreshold,
                        silenceThreshold: this.options.silenceThreshold,
                        silenceDuration: this.options.silenceDuration,
                        minSpeechDuration: this.options.minSpeechDuration
                    });
                    
                    // Test VAD initialization
                    const vadStarted = await this.vad.start();
                    if (!vadStarted) {
                        throw new Error('VAD failed to start');
                    }
                    
                    // Stop VAD after test
                    await this.vad.stop();
                    
                    this.setupVADCallbacks();
                    this.isVADAvailable = true;
                    
                    console.log('🎤 VADSpeechEngine: VAD initialized successfully');
                } catch (vadError) {
                    console.warn('🎤 VADSpeechEngine: VAD initialization failed, disabling VAD:', vadError);
                    this.options.vadEnabled = false;
                    this.isVADAvailable = false;
                    this.vadInitializationFailed = true;
                    this.emit(SpeechEvents.VAD_UNAVAILABLE, { reason: 'initialization_failed', error: vadError });
                    
                    // Cleanup failed VAD
                    if (this.vad) {
                        this.vad.destroy();
                        this.vad = null;
                    }
                }
            }
            
            // Initialize speech engine (always needed as fallback)
            console.log('🎤 VADSpeechEngine: Initializing speech engine...');
            this.speechEngine = await this.createSpeechEngine();
            
        // Initialize trigger detector with exact 2-token wake phrase
        console.log('🎤 VADSpeechEngine: Initializing trigger detector...');
        this.triggerDetector = new TriggerDetector({
            wakeSequences: [['hey', 'chef']],
            requireConsecutiveMatches: true,
            consecutiveMatchCount: 1,
            maxGapTokens: 0,
            windowMs: 4000,
            refractoryPeriod: 3000
        });
        this.setupTriggerDetectorCallbacks();
            
            this.setupSpeechEngineCallbacks();
            this.setupStateMachine();
            
            // Check support based on the configured speech engine type
            const engineType = this.options.speechEngineType;
            if (engineType === 'streaming') {
                this.isSupported = StreamingSTT.isSupported();
            } else {
                this.isSupported = BrowserSTT.isSupported();
            }
            this.isInitialized = true;
            
            console.log('🎤 VADSpeechEngine: Initialized successfully', {
                vadEnabled: this.options.vadEnabled,
                isVADAvailable: this.isVADAvailable,
                isSupported: this.isSupported,
                fallbackMode: !this.isVADAvailable
            });
            
        } catch (error) {
            console.error('🎤 VADSpeechEngine: Initialization failed:', error);
            this.isSupported = false;
            this.isVADAvailable = false;
            this.vadInitializationFailed = true;
            this.emit(SpeechEvents.VAD_UNAVAILABLE, { reason: 'initialization_failed', error });
        }
    }

    /**
     * Setup VAD callbacks
     */
    setupVADCallbacks() {
        if (!this.vad) return;
        
        this.vad.setOnSpeechStart((event) => {
            console.log('🎤 VADSpeechEngine: VAD detected speech start');
            this.handleVADSpeechStart(event);
        });
        
        this.vad.setOnSpeechEnd((event) => {
            console.log('🎤 VADSpeechEngine: VAD detected speech end');
            this.handleVADSpeechEnd(event);
        });
        
        this.vad.setOnEnergyUpdate((event) => {
            // Optional: Log energy levels for debugging
            // console.log('🎤 VADSpeechEngine: Energy update:', event.smoothedEnergy);
        });
        
        this.vad.setOnError((error) => {
            console.error('🎤 VADSpeechEngine: VAD error:', error);
            this.handleError(error);
        });
    }

    /**
     * Create the appropriate speech engine based on configuration
     * @returns {Promise<SpeechEngine>} The created speech engine
     */
    async createSpeechEngine() {
        const engineType = this.options.speechEngineType;
        
        switch (engineType) {
            case 'streaming':
                console.log('🎤 VADSpeechEngine: Creating StreamingSTT engine');
                return new StreamingSTT({
                    language: this.options.language,
                    maxAlternatives: this.options.maxAlternatives,
                    sampleRate: 16000, // VAD works with 16kHz
                    format: 'pcm'
                });
                
            case 'browser':
            default:
                console.log('🎤 VADSpeechEngine: Creating BrowserSTT engine');
                return new BrowserSTT({
                    language: this.options.language,
                    maxAlternatives: this.options.maxAlternatives
                });
        }
    }

    /**
     * Set partial result callback (called by SpeechManager)
     * @param {Function} callback - Callback function
     */
    setOnPartial(callback) {
        this.onPartial = callback;
    }

    /**
     * Set final result callback (called by SpeechManager)
     * @param {Function} callback - Callback function
     */
    setOnFinal(callback) {
        this.onFinal = callback;
    }

    /**
     * Set error callback (called by SpeechManager)
     * @param {Function} callback - Callback function
     */
    setOnError(callback) {
        this.onError = callback;
    }

    /**
     * Set state change callback (called by SpeechManager)
     * @param {Function} callback - Callback function
     */
    setOnStateChange(callback) {
        this.onStateChange = callback;
    }

    /**
     * Setup trigger detector callbacks
     */
    setupTriggerDetectorCallbacks() {
        if (!this.triggerDetector) return;
        
        this.triggerDetector.setOnTriggerDetected((event) => {
            console.log('🎯 Trigger DETECTED (VAD): "hey chef"');
            // Handle trigger detection - emit to SpeechManager
            if (this.onTrigger) {
                this.onTrigger('hey chef');
            }
        });
        
        this.triggerDetector.setOnTriggerRejected((event) => {
            console.log('🎯 VADSpeechEngine: Trigger rejected:', event.phrase, 'reason:', event.reason);
        });
    }

    /**
     * Setup speech engine callbacks
     */
    setupSpeechEngineCallbacks() {
        if (!this.speechEngine) return;
        
        this.speechEngine.setOnPartial((result) => {
            console.log('🎤 VADSpeechEngine: Received partial result:', result);
            this.handlePartialResult(result);
        });
        
        this.speechEngine.setOnFinal((result) => {
            console.log('🎤 VADSpeechEngine: Received final result:', result);
            this.handleFinalResult(result);
        });
        
        this.speechEngine.setOnError((error) => {
            this.handleError(error);
        });
        
        this.speechEngine.setOnStateChange((stateChange) => {
            this.handleStateChange(stateChange);
        });
    }

    /**
     * Setup state machine callbacks
     */
    setupStateMachine() {
        this.stateMachine.setStateChangeCallback((stateChange) => {
            if (this.onStateChange) {
                this.onStateChange(stateChange);
            }
        });
    }

    /**
     * Handle VAD speech start event
     * @param {Object} event - Speech start event
     */
    handleVADSpeechStart(event) {
        if (!this.options.autoStartOnSpeech) return;
        
        this.speechStartTime = event.timestamp;
        
        // Start speech recognition if not already listening
        if (!this.stateMachine.isListening() && !this.isManualStop) {
            console.log('🎤 VADSpeechEngine: VAD detected speech start, starting recognition');
            this.startSpeechRecognition();
        } else if (this.stateMachine.isListening() && this.pendingRestart) {
            // If we're waiting for a restart, start immediately
            console.log('🎤 VADSpeechEngine: VAD detected speech during pending restart, starting recognition');
            this.startSpeechRecognition();
        }
    }

    /**
     * Handle VAD speech end event
     * @param {Object} event - Speech end event
     */
    handleVADSpeechEnd(event) {
        if (!this.options.autoStopOnSilence) return;
        
        console.log('🎤 VADSpeechEngine: VAD detected speech end, preparing to flush segment');
        
        // Mark that we should stop after current recognition ends
        this.pendingRestart = false;
        
        // Stop speech recognition after a brief delay to capture final words
        setTimeout(() => {
            if (this.stateMachine.isListening() && !this.isManualStop) {
                console.log('🎤 VADSpeechEngine: Flushing final segment due to VAD speech end');
                this.flushCurrentSegment();
            }
        }, 500); // Small delay to capture final words
    }

    /**
     * Flush current speech segment as final result
     */
    flushCurrentSegment() {
        if (this.currentSegment && this.currentSegment.text.trim()) {
            console.log('🎤 VADSpeechEngine: Flushing final segment:', this.currentSegment.text);
            
            // Add to segments buffer
            this.speechSegments.push({
                ...this.currentSegment,
                timestamp: Date.now(),
                isFinal: true
            });
            
            // Emit final result
            if (this.onFinal) {
                console.log('🎤 VADSpeechEngine: Calling SpeechManager onFinal callback with:', this.currentSegment.text);
                this.onFinal({
                    transcript: this.currentSegment.text, // Use 'transcript' for SpeechManager compatibility
                    text: this.currentSegment.text,
                    confidence: this.currentSegment.confidence,
                    timestamp: this.currentSegment.timestamp,
                    isVADGated: true
                });
            } else {
                console.warn('🎤 VADSpeechEngine: No onFinal callback set - SpeechManager not connected!');
            }
            
            // Clear current segment
            this.currentSegment = null;
        }
        
        // Clear partial buffer
        this.partialBuffer = '';
    }
    
    /**
     * Start speech recognition - idempotent, no re-acquire
     */
    async startSpeechRecognition() {
        if (!this.speechEngine) {
            console.warn('🎤 VADSpeechEngine: No speech engine available');
            return;
        }
        
        const currentState = this.stateMachine.getCurrentState();
        
        // Idempotent: if already listening, no-op with debug line
        if (currentState === MinimalStates.LISTENING) {
            console.log('🎤 VADSpeechEngine: Already listening, ignoring start request');
            return;
        }
        
        // Debounce: ignore if stopping or error
        if (currentState === MinimalStates.STOPPING) {
            console.log('🎤 VADSpeechEngine: Ignoring speech start request - currently stopping');
            return;
        }
        
        if (currentState === MinimalStates.ERROR) {
            console.log('🎤 VADSpeechEngine: Ignoring speech start request - in error state');
            return;
        }
        
        try {
            // Clear any pending restart flag
            this.pendingRestart = false;
            
            // Resume AudioContext if suspended (no mic re-acquire)
            await micManager.resumeIfSuspended();
            
            // Start the speech engine
            await this.speechEngine.start();
            
            // Direct transition to listening (no mic re-acquire)
            await this.stateMachine.transitionTo(MinimalStates.LISTENING, TransitionReasons.VAD_SPEECH_START);
            
            console.log(`🎤 VAD start: ${currentState} → listening (no re-acquire)`);
        } catch (error) {
            console.error('🎤 VADSpeechEngine: Failed to start speech recognition:', error);
            // Try to transition to error state, but don't fail if that also fails
            try {
                await this.stateMachine.transitionTo(MinimalStates.ERROR, TransitionReasons.ERROR_OCCURRED, { error });
            } catch (transitionError) {
                console.warn('🎤 VADSpeechEngine: Failed to transition to error state:', transitionError);
            }
        }
    }

    /**
     * Stop speech recognition
     */
    async stopSpeechRecognition() {
        if (!this.speechEngine || !this.stateMachine.isListening()) {
            return;
        }
        
        try {
            const currentState = this.stateMachine.getCurrentState();
            
            // If we're in an error state, just try to stop the speech engine and reset to idle
            if (currentState === MinimalStates.ERROR) {
                console.log('🎤 VADSpeechEngine: Stopping speech engine from error state');
                await this.speechEngine.stop();
                await this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.ERROR_RECOVERED);
                return;
            }
            
            // In VAD-controlled mode, we don't stop immediately - we let BrowserSTT end naturally
            if (this.isVADControlled) {
                console.log('🎤 VADSpeechEngine: VAD-controlled stop - letting BrowserSTT end naturally');
                // Mark that we should not restart after BrowserSTT ends
                this.pendingRestart = false;
                // Don't call speechEngine.stop() - let it end naturally with continuous: false
                return;
            }
            
            await this.speechEngine.stop();
            await this.stateMachine.transitionTo(MinimalStates.ENDPOINTED, TransitionReasons.VAD_SPEECH_END);
            
            console.log('🎤 VADSpeechEngine: Speech recognition stopped via VAD');
        } catch (error) {
            console.error('🎤 VADSpeechEngine: Failed to stop speech recognition:', error);
            // Try to transition to error state, but don't fail if that also fails
            try {
                await this.stateMachine.transitionTo(MinimalStates.ERROR, TransitionReasons.ERROR_OCCURRED, { error });
            } catch (transitionError) {
                console.warn('🎤 VADSpeechEngine: Failed to transition to error state:', transitionError);
            }
        }
    }

    /**
     * Handle partial speech results
     * @param {Object} result - Partial result
     */
    /**
     * Normalize transcript for trigger detection
     * @param {string} transcript - Raw transcript
     * @returns {string} Normalized transcript
     */
    normalizeTranscript(transcript) {
        return transcript
            .toLowerCase()
            .replace(/[^\w\s]/g, ' ') // Remove punctuation
            .replace(/\s+/g, ' ') // Normalize whitespace
            .trim();
    }

    handlePartialResult(result) {
        this.partialBuffer = result.transcript;
        
        // Feed to trigger detector for advanced trigger detection
        if (this.triggerDetector) {
            this.triggerDetector.pushPartial(this.normalizeTranscript(result.transcript));
        }
        
        // Update current segment with latest partial result
        if (this.isVADControlled) {
            this.currentSegment = {
                text: result.transcript,
                confidence: result.confidence || 0,
                timestamp: result.timestamp || Date.now()
            };
        }
        
        if (this.onPartial) {
            console.log('🎤 VADSpeechEngine: Calling SpeechManager onPartial callback with:', result.transcript);
            this.onPartial({
                ...result,
                isVADGated: this.isVADControlled
            });
        } else {
            console.warn('🎤 VADSpeechEngine: No onPartial callback set - SpeechManager not connected!');
        }
    }

    /**
     * Handle final speech results
     * @param {Object} result - Final result
     */
    handleFinalResult(result) {
        const finalText = result.transcript;
        console.log('🎤 VADSpeechEngine: Final result:', finalText);
        
        if (this.isVADControlled) {
            // In VAD-controlled mode, we buffer results and flush on VAD endpoint
            if (finalText.trim()) {
                this.currentSegment = {
                    text: finalText,
                    confidence: result.confidence || 0,
                    timestamp: result.timestamp || Date.now()
                };
                
                // If VAD has already ended, flush immediately
                if (!this.pendingRestart) {
                    console.log('🎤 VADSpeechEngine: VAD ended, flushing final result immediately');
                    this.flushCurrentSegment();
                } else {
                    console.log('🎤 VADSpeechEngine: Buffering final result, waiting for VAD endpoint');
                }
            }
        } else {
            // In non-VAD mode, emit immediately
            if (this.onFinal) {
                this.onFinal({
                    transcript: result.transcript, // Ensure transcript field exists
                    ...result,
                    isVADGated: false
                });
            }
        }
        
        // Clear partial buffer
        this.partialBuffer = '';
        
        // Handle non-continuous mode restart
        if (this.isVADControlled && this.pendingRestart) {
            console.log('🎤 VADSpeechEngine: BrowserSTT ended, waiting for VAD to restart');
            // Don't restart immediately - wait for VAD speech start
        }
    }

    /**
     * Handle errors
     * @param {Object} error - Error object
     */
    handleError(error) {
        console.error('🎤 VADSpeechEngine: Error:', error);
        
        if (this.onError) {
            this.onError(error);
        }
    }
    
    /**
     * Get VAD gating status
     * @returns {Object} VAD gating status information
     */
    getVADGatingStatus() {
        return {
            isVADControlled: this.isVADControlled,
            pendingRestart: this.pendingRestart,
            currentSegment: this.currentSegment,
            speechSegments: this.speechSegments,
            partialBuffer: this.partialBuffer,
            vadEnabled: this.options.vadEnabled,
            isVADAvailable: this.isVADAvailable
        };
    }
    
    /**
     * Force recovery from error state
     * @returns {Promise<boolean>} Success status
     */
    async recoverFromError() {
        try {
            const currentState = this.stateMachine.getCurrentState();
            
            if (currentState === MinimalStates.ERROR) {
                console.log('🎤 VADSpeechEngine: Forcing recovery from error state');
                
                // Stop any running speech engine
                if (this.speechEngine) {
                    try {
                        await this.speechEngine.stop();
                    } catch (stopError) {
                        console.warn('🎤 VADSpeechEngine: Error stopping speech engine during recovery:', stopError);
                    }
                }
                
                // Reset to idle state
                await this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.ERROR_RECOVERED);
                
                console.log('🎤 VADSpeechEngine: Successfully recovered from error state');
                return true;
            } else {
                console.log(`🎤 VADSpeechEngine: Not in error state (current: ${currentState}), no recovery needed`);
                return true;
            }
        } catch (error) {
            console.error('🎤 VADSpeechEngine: Failed to recover from error state:', error);
            return false;
        }
    }

    /**
     * Handle state changes
     * @param {Object} stateChange - State change object
     */
    handleStateChange(stateChange) {
        try {
            const currentState = this.stateMachine.getCurrentState();
            
            // Map SpeechStateMachine states to MinimalStateMachine states
            const stateMapping = {
                'idle': MinimalStates.IDLE,
                'acquiring_mic': MinimalStates.ACQUIRING_MIC,
                'listening': MinimalStates.LISTENING,
                'endpointed': MinimalStates.ENDPOINTED,
                'error': MinimalStates.ERROR
            };
            
            const targetState = stateMapping[stateChange.current];
            if (!targetState) {
                console.warn('🎤 VADSpeechEngine: Unknown state from speech engine:', stateChange.current);
                return;
            }
            
            // Don't try to transition if we're already in the target state
            if (currentState === targetState) {
                console.log(`🎤 VADSpeechEngine: Already in ${targetState} state, ignoring state change`);
                return;
            }
            
            // Special handling for error state - only allow recovery transitions
            if (currentState === MinimalStates.ERROR) {
                if (targetState === MinimalStates.IDLE) {
                    // Allow recovery from error to idle
                    console.log('🎤 VADSpeechEngine: Recovering from error state to idle');
                    this.stateMachine.transitionTo(targetState, TransitionReasons.ERROR_RECOVERED);
                } else {
                    // Ignore other transitions from error state
                    console.log(`🎤 VADSpeechEngine: Ignoring transition from error to ${targetState} - must recover to idle first`);
                }
                return;
            }
            
            // Special handling for BrowserSTT ending in non-continuous mode
            if (targetState === MinimalStates.ENDPOINTED && this.isVADControlled) {
                console.log('🎤 VADSpeechEngine: BrowserSTT ended in non-continuous mode');
                
                // If we're still expecting speech (pendingRestart), mark for restart
                if (this.pendingRestart) {
                    console.log('🎤 VADSpeechEngine: Marking for restart after BrowserSTT ended');
                    // Don't transition to endpointed - stay in ready state for next speech
                    return;
                } else {
                    // VAD has ended, transition to endpointed
                    console.log('🎤 VADSpeechEngine: VAD ended, transitioning to endpointed');
                    this.stateMachine.transitionTo(targetState, TransitionReasons.SPEECH_ENGINE_STATE_CHANGE);
                    return;
                }
            }
            
            // For non-error states, check if the transition is valid
            if (this.stateMachine.isValidTransition(currentState, targetState)) {
                this.stateMachine.transitionTo(targetState, TransitionReasons.SPEECH_ENGINE_STATE_CHANGE);
            } else {
                console.log(`🎤 VADSpeechEngine: Invalid transition from ${currentState} to ${targetState}, ignoring`);
            }
        } catch (error) {
            console.warn('🎤 VADSpeechEngine: Failed to handle state change:', error);
        }
    }

    /**
     * Start speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async start() {
        if (!this.isSupported || !this.isInitialized || !this.speechEngine) {
            console.error('🎤 VADSpeechEngine: Not supported or not initialized', {
                isSupported: this.isSupported,
                isInitialized: this.isInitialized,
                hasSpeechEngine: !!this.speechEngine
            });
            return false;
        }

        // Ensure microphone is acquired through MicManager
        try {
            console.log('🎤 VADSpeechEngine: Ensuring microphone access...');
            await micManager.acquire();
            
            // Validate MicManager state
            if (!micManager.validate()) {
                throw new Error('MicManager validation failed');
            }
            
            console.log('🎤 VADSpeechEngine: Microphone access validated');
        } catch (error) {
            console.error('🎤 VADSpeechEngine: Failed to acquire microphone:', error);
            return false;
        }

        try {
            this.isManualStop = false;
            this.partialBuffer = '';
            // Start with acquiring_mic only if we need to acquire mic
            await this.stateMachine.transitionTo(MinimalStates.ACQUIRING_MIC, TransitionReasons.START_REQUESTED);
            
            // Start VAD first if available
            if (this.isVADAvailable && this.vad) {
                try {
                    console.log('🎤 VADSpeechEngine: Starting VAD...');
                    const vadStarted = await this.vad.start();
                    if (!vadStarted) {
                        throw new Error('VAD failed to start');
                    }
                    console.log('🎤 VADSpeechEngine: VAD started successfully');
                } catch (vadError) {
                    console.warn('🎤 VADSpeechEngine: VAD start failed, falling back to direct speech recognition:', vadError);
                    this.isVADAvailable = false;
                    this.emit(SpeechEvents.VAD_UNAVAILABLE, { reason: 'start_failed', error: vadError });
                }
            }
            
            // Start speech recognition
            if (this.isVADAvailable && this.options.autoStartOnSpeech) {
                console.log('🎤 VADSpeechEngine: Waiting for speech to start recognition...');
                // Transition to ready state - waiting for VAD to trigger speech recognition
                await this.stateMachine.transitionTo(MinimalStates.READY, TransitionReasons.MIC_ACQUIRED);
            } else {
                console.log('🎤 VADSpeechEngine: Starting speech recognition directly (VAD unavailable or disabled)...');
                await this.startSpeechRecognition();
                // Transition to listening state
                await this.stateMachine.transitionTo(MinimalStates.LISTENING, TransitionReasons.LISTENING_STARTED);
            }
            
            return true;
        } catch (error) {
            console.error('🎤 VADSpeechEngine: Failed to start:', error);
            await this.stateMachine.transitionTo(MinimalStates.ERROR, TransitionReasons.ERROR_OCCURRED, { error });
            return false;
        }
    }

    /**
     * Stop speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async stop() {
        try {
            this.isManualStop = true;
            
            // Check current state and handle appropriately
            const currentState = this.stateMachine.getCurrentState();
            
            if (currentState === MinimalStates.IDLE) {
                // Already idle, no need to stop
                return true;
            }
            
            // Transition to stopping
            await this.stateMachine.transitionTo(MinimalStates.STOPPING, TransitionReasons.STOP_REQUESTED);
            
            // Stop speech recognition
            if (this.speechEngine) {
                await this.speechEngine.stop();
            }
            
            // Stop VAD
            if (this.vad) {
                await this.vad.stop();
            }
            
            // Transition to idle
            await this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.STOPPING_COMPLETE);
            
            console.log('🎤 VADSpeechEngine: Stopped successfully');
            return true;
        } catch (error) {
            console.error('🎤 VADSpeechEngine: Failed to stop:', error);
            // Try to force transition to idle for cleanup
            try {
                await this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.ERROR_RECOVERED);
            } catch (transitionError) {
                console.warn('🎤 VADSpeechEngine: Failed to transition to idle after stop error:', transitionError);
            }
            return false;
        }
    }

    /**
     * Check if currently listening
     * @returns {boolean} Listening status
     */
    isListening() {
        return this.stateMachine.isListening();
    }

    /**
     * Get current state
     * @returns {string} Current state
     */
    getState() {
        return this.stateMachine.getCurrentState();
    }

    /**
     * Update VAD thresholds
     * @param {Object} thresholds - New threshold values
     */
    updateVADThresholds(thresholds) {
        if (this.vad) {
            this.vad.updateThresholds(thresholds);
        }
    }

    /**
     * Get VAD availability status
     * @returns {Object} VAD status information
     */
    getVADStatus() {
        return {
            isVADAvailable: this.isVADAvailable,
            vadInitializationFailed: this.vadInitializationFailed,
            vadEnabled: this.options.vadEnabled,
            hasVAD: !!this.vad,
            thresholds: {
                energyThreshold: this.options.energyThreshold,
                silenceThreshold: this.options.silenceThreshold,
                silenceDuration: this.options.silenceDuration,
                minSpeechDuration: this.options.minSpeechDuration
            }
        };
    }

    /**
     * Get VAD state
     * @returns {Object} VAD state
     */
    getVADState() {
        return this.vad ? this.vad.getState() : null;
    }

    /**
     * Check if speech recognition is supported
     * @returns {boolean} Support status
     */
    isSupported() {
        return this.isSupported;
    }

    /**
     * Get browser compatibility info
     * @returns {Object} Compatibility info
     */
    getBrowserInfo() {
        const baseInfo = this.speechEngine ? this.speechEngine.getBrowserInfo() : {};
        
        return {
            ...baseInfo,
            vadSupported: VoiceActivityDetector.isSupported(),
            vadEnabled: this.options.vadEnabled,
            isVADSpeechEngine: true
        };
    }

    /**
     * Cleanup resources
     */
    destroy() {
        this.stop();
        
        if (this.vad) {
            this.vad.destroy();
            this.vad = null;
        }
        
        if (this.speechEngine) {
            this.speechEngine.destroy();
            this.speechEngine = null;
        }
        
        if (this.triggerDetector) {
            this.triggerDetector.reset();
            this.triggerDetector = null;
        }
        
        // Transition to idle with cleanup reason to avoid transition errors
        if (this.stateMachine.getCurrentState() !== MinimalStates.IDLE) {
            this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.CLEANUP);
        }
        
        this.stateMachine.destroy();
        super.destroy();
    }
}
